import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { apiClient } from '../../services/api';
import { toast } from 'react-toastify';
import NotificationRecipientFields from './NotificationRecipientFields';
import {
  channelUsesEmail,
  channelUsesSms,
  channelUsesWhatsApp,
  NOTIFICATION_CHANNEL_OPTIONS,
  type NotificationChannelSelection,
} from '../../utils/notificationChannels';

interface SendSummaryModalProps {
  open: boolean;
  onClose: () => void;
  date: string;
  campaignId?: string;
  supervisorId?: string;
  defaultRecipient?: string;
}

const SendSummaryModal: React.FC<SendSummaryModalProps> = ({
  open,
  onClose,
  date,
  campaignId,
  supervisorId,
  defaultRecipient,
}) => {
  const [channel, setChannel] = useState<NotificationChannelSelection>('email');
  const [recipients, setRecipients] = useState(defaultRecipient || '');
  const [smsRecipients, setSmsRecipients] = useState('');
  const [defaultPhone, setDefaultPhone] = useState('');
  const [includeCampaignSummary, setIncludeCampaignSummary] = useState(true);
  const [includeUncoveredPositions, setIncludeUncoveredPositions] = useState(true);
  const [includeCoveredAbsences, setIncludeCoveredAbsences] = useState(true);
  const [message, setMessage] = useState('');
  const [preview, setPreview] = useState<any>(null);
  const [deliveryStatus, setDeliveryStatus] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setRecipients(defaultRecipient || '');
    setSmsRecipients('');
    setPreview(null);
    Promise.all([
      apiClient.notificationSettings.getStatus(),
      apiClient.userNotificationPreferences.get().catch(() => null),
    ]).then(([statusRes, prefsRes]) => {
      setDeliveryStatus(statusRes.data.data);
      const phone = prefsRes?.data?.data?.phoneNumber;
      if (phone) {
        setDefaultPhone(phone);
        setSmsRecipients(phone);
      }
    }).catch(() => setDeliveryStatus(null));
  }, [open, defaultRecipient]);

  const buildPayload = () => ({
    date,
    campaignId: campaignId || undefined,
    supervisorId: supervisorId || undefined,
    channel,
    recipients: recipients.split(',').map((r) => r.trim()).filter(Boolean),
    smsRecipients: smsRecipients.split(',').map((r) => r.trim()).filter(Boolean),
    includeCampaignSummary,
    includeUncoveredPositions,
    includeCoveredAbsences,
    message,
  });

  const validateRecipients = () => {
    const payload = buildPayload();
    if (channelUsesEmail(channel) && payload.recipients.length === 0) {
      toast.error('Enter at least one email recipient');
      return false;
    }
    if ((channelUsesSms(channel) || channelUsesWhatsApp(channel)) && payload.smsRecipients.length === 0) {
      toast.error('Enter at least one phone number (e.g. +12125551234)');
      return false;
    }
    if (channelUsesSms(channel) && deliveryStatus && !deliveryStatus.smsReady) {
      toast.error('SMS is not ready. Enable SMS and configure Twilio in Notification Settings.');
      return false;
    }
    if (channelUsesWhatsApp(channel) && deliveryStatus && !deliveryStatus.whatsappReady) {
      toast.error('WhatsApp is not ready. Enable WhatsApp and configure the Twilio sandbox sender in Notification Settings.');
      return false;
    }
    return true;
  };

  const handlePreview = async () => {
    if (!validateRecipients()) return;
    try {
      setLoading(true);
      const response = await apiClient.operations.previewSummary(buildPayload());
      setPreview(response.data.data);
      if (response.data.data?.deliveryStatus) {
        setDeliveryStatus(response.data.data.deliveryStatus);
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to generate preview');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!validateRecipients()) return;
    try {
      setLoading(true);
      const response = await apiClient.operations.sendSummary(buildPayload());
      const results = response.data.data?.results || [];
      const failed = results.filter((r: any) => r.status === 'failed');
      const skipped = results.filter((r: any) => r.status === 'skipped');

      if (failed.length) {
        toast.warning(`${response.data.message || 'Sent with failures'}: ${failed.map((r: any) => r.error).join('; ')}`);
      } else if (skipped.length) {
        toast.warning(response.data.message || 'Delivery skipped — check Notification Settings');
      } else {
        toast.success(response.data.message || 'Summary sent');
      }
      onClose();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to send summary');
    } finally {
      setLoading(false);
    }
  };

  const smsReady = deliveryStatus?.smsReady;
  const whatsappReady = deliveryStatus?.whatsappReady;
  const showSmsWarning = channelUsesSms(channel) && deliveryStatus && !smsReady;
  const showWhatsAppWarning = channelUsesWhatsApp(channel) && deliveryStatus && !whatsappReady;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Send Attendance & Coverage Summary</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {showSmsWarning && (
            <Grid item xs={12}>
              <Alert severity="warning">
                SMS is not ready. An administrator must enable SMS and configure Twilio
                (Account SID, Auth Token, and From Number or Messaging Service SID) under Notification Settings.
              </Alert>
            </Grid>
          )}
          {showWhatsAppWarning && (
            <Grid item xs={12}>
              <Alert severity="warning">
                WhatsApp is not ready. Configure Twilio under Notification Settings, set the sandbox sender
                (whatsapp:+14155238886), and enable WhatsApp.
              </Alert>
            </Grid>
          )}
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Channel</InputLabel>
              <Select value={channel} label="Channel" onChange={(e) => setChannel(e.target.value as NotificationChannelSelection)}>
                {NOTIFICATION_CHANNEL_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <NotificationRecipientFields
              channel={channel}
              emailRecipients={recipients}
              smsRecipients={smsRecipients}
              onEmailRecipientsChange={setRecipients}
              onSmsRecipientsChange={setSmsRecipients}
              defaultEmail={defaultRecipient}
              defaultPhone={defaultPhone}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel control={<Checkbox checked={includeCampaignSummary} onChange={(e) => setIncludeCampaignSummary(e.target.checked)} />} label="Include campaign summary" />
            <FormControlLabel control={<Checkbox checked={includeUncoveredPositions} onChange={(e) => setIncludeUncoveredPositions(e.target.checked)} />} label="Include uncovered positions" />
            <FormControlLabel control={<Checkbox checked={includeCoveredAbsences} onChange={(e) => setIncludeCoveredAbsences(e.target.checked)} />} label="Include covered absences" />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline minRows={2} label="Optional message" value={message} onChange={(e) => setMessage(e.target.value)} />
          </Grid>
          {preview && (
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 1 }}>Preview</Alert>
              <Box sx={{ bgcolor: '#f7f9fc', p: 2, borderRadius: 1 }}>
                {channelUsesEmail(channel) && (
                  <>
                    <Typography variant="subtitle2">Email Subject</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>{preview.email?.subject}</Typography>
                    <Typography variant="subtitle2">Email Body</Typography>
                    <Typography variant="body2" component="div" sx={{ mb: 2 }} dangerouslySetInnerHTML={{ __html: preview.email?.body || '' }} />
                  </>
                )}
                {(channelUsesSms(channel) || channelUsesWhatsApp(channel)) && (
                  <>
                    {channelUsesSms(channel) && (
                      <>
                        <Typography variant="subtitle2">SMS → {smsRecipients || '—'}</Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: channelUsesWhatsApp(channel) ? 2 : 0 }}>{preview.sms?.body}</Typography>
                      </>
                    )}
                    {channelUsesWhatsApp(channel) && (
                      <>
                        <Typography variant="subtitle2">WhatsApp → {smsRecipients || '—'}</Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{preview.sms?.body}</Typography>
                      </>
                    )}
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      {preview.sms?.body?.length || 0} characters
                    </Typography>
                  </>
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handlePreview} disabled={loading}>Preview</Button>
        <Button variant="contained" onClick={handleSend} disabled={loading || showSmsWarning || showWhatsAppWarning}>
          {loading ? 'Sending...' : 'Send'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendSummaryModal;
